'use client';

import { useEffect, useRef } from 'react';
import * as Sentry from '@sentry/nextjs';
import { NAVIGATION_URLS } from '@libs/core';
import { Picture } from '@petsmart-ui/sparky';
import { BREAKPOINTS } from '@libs/common-next';
import { PICTURES } from '../../constants';
import type { GlobalErrorProps } from './types';

// GlobalErrorBody is used as:
// 1. As a body of GlobalErrorPage, used in global-error.tsx in HCP, ACP, PLP, PDP, cart, checkout
// 2. error.tsx fallback component
// 3. Part of a static stand-alone error page.
export default function GlobalErrorBody({ error }: GlobalErrorProps) {
    const IMAGE_SIZES = {
        SizeSm: 250,
        SizeMd: 350,
        SizeLg: 400,
    };
    const notFoundImage = PICTURES.NOT_FOUND.SRC;
    const loggedErrors = useRef<string[]>([]);

    useEffect(() => {
        if (error && error.digest && !loggedErrors.current.includes(error.digest)) {
            loggedErrors.current.push(error.digest);
            Sentry.captureException(error, { extra: { ...error } });
        }
    }, [error]);

    return (
        <div data-testid="error-body">
            {/* We use style tag since .css files are not available for use in global-error.tsx */}
            <style>
                {`
                    .body-container {
                        padding: 32px 16px;
                        width: 100%;
                        font: normal normal 200 16px/24px Euclid Circular B;
                        color: var(--sparky-color-neutral-900);
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        text-align: center;
                    }

                    .text-container {
                        display: flex;
                        flex-direction: column;
                        min-width: ${IMAGE_SIZES.SizeSm}px;
                        width: ${IMAGE_SIZES.SizeSm}px;
                    }

                    .text-container__header {
                        margin: 0;
                        font-size: 36px;
                        line-height: 44px;
                    }

                    .text-container__subtext {
                        margin-top: 16px;
                        font-weight: 600;
                        font-size: 18px;
                        line-height: 24px;
                    }

                    .body-container__image {
                        display: block;
                        margin: 0 auto;
                        margin-top: 16px;
                        min-width: ${IMAGE_SIZES.SizeSm}px;
                        width: ${IMAGE_SIZES.SizeSm}px;
                        height: ${IMAGE_SIZES.SizeSm}px;
                    }

                    .btn-container {
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        width: 100%;
                    }

                    .btn-container__button {
                        width: 100%;
                        min-width: ${IMAGE_SIZES.SizeSm}px;
                        height: 48px;
                        margin-top: 16px;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        border-radius: 4px;
                        cursor: pointer;
                        background-color: var(--sparky-theme-color-primary-background);
                        color: #FFFFFF;
                        border: none;
                        padding: 14px 24px;
                        text-decoration: none;
                    }

                    .btn-container__button:hover {
                        background-color: var(--sparky-theme-color-primary-hover);;
                    }

                    .btn-container__button:focus {
                        background-color: var(--sparky-theme-color-primary-focus);
                    }

                    @media (min-width: 560px) {
                        .body-container {
                            padding: 32px;
                        }

                        .text-container {
                            width: ${IMAGE_SIZES.SizeMd}px;
                        }
                        .body-container__image {
                            width: ${IMAGE_SIZES.SizeMd}px;
                            height: ${IMAGE_SIZES.SizeMd}px;
                        }

                        .text-container__subtext {
                            white-space: nowrap
                        }
                    }

                    @media (min-width: 960px) {
                        .body-container {
                            padding: 64px;
                        }

                        .text-container {
                            width: ${IMAGE_SIZES.SizeLg}px;
                        }

                        .text-container__header {
                            font-size: 40px;
                            line-height: 48px;
                        }

                        .text-container__subtext {
                            font-size: 20px;
                            line-height: 28px;
                        }

                        .body-container__image {
                            width: ${IMAGE_SIZES.SizeLg}px;
                            height: ${IMAGE_SIZES.SizeLg}px;
                        }

                        .btn-container__button {
                            width: auto;
                            min-width: auto;
                        }
                    }
                `}
            </style>
            <div className="body-container">
                <div className="text-container">
                    <h1 className="text-container__header">Oops!</h1>
                    <div className="text-container__subtext" data-testid="global-error">
                        {error?.digest ? <div>Error {error?.digest}</div> : null}
                        <div>Something went wrong. Please try again.</div>
                    </div>
                </div>
                <Picture
                    className="body-container__image"
                    imgSrc={`${notFoundImage}?fmt=webp&wid=250&hei=250`}
                    items={[
                        {
                            srcSet: `${notFoundImage}?fmt=webp&wid=${IMAGE_SIZES.SizeLg}&hei=${
                                IMAGE_SIZES.SizeLg
                            } 1x, ${notFoundImage}?fmt=webp&wid=${
                                IMAGE_SIZES.SizeLg * 2
                            }&hei=${IMAGE_SIZES.SizeLg * 2} 2x`,
                            media: `(min-width: ${BREAKPOINTS.lg}px)`,
                        },
                        {
                            srcSet: `${notFoundImage}?fmt=webp&wid=${IMAGE_SIZES.SizeMd}&hei=${
                                IMAGE_SIZES.SizeMd
                            } 1x, ${notFoundImage}?fmt=webp&wid=${
                                IMAGE_SIZES.SizeMd * 2
                            }&hei=${IMAGE_SIZES.SizeMd * 2} 2x`,
                            media: `(min-width: ${BREAKPOINTS.md}px)`,
                        },
                        {
                            srcSet: `${notFoundImage}?fmt=webp&wid=${IMAGE_SIZES.SizeSm}&hei=${
                                IMAGE_SIZES.SizeSm
                            } 1x, ${notFoundImage}?fmt=webp&wid=${
                                IMAGE_SIZES.SizeSm * 2
                            }&hei=${IMAGE_SIZES.SizeSm * 2} 2x`,
                            media: `(min-width: ${BREAKPOINTS.sm2}px)`,
                        },
                    ]}
                    imgAlt="Sad dog"
                    data-testid="error-picture"
                />
                <div className="btn-container">
                    {/*  we want to have both reset button and navigational link.
                    We use reset in the context of ErrorBoundary and reset the error state for the current component without reloading the entire page.
                    In the case where we use this component as a stand-alone page, we dont have reset, and we want to reload the entire page */}
                    {/* {reset ? (
                        <button
                            type="button"
                            className="btn-container__button"
                            aria-label="Reset button"
                            data-testid="refresh-btn"
                            onClick={reset}
                        >
                            Continue shopping
                        </button>
                    ) : ( */}
                    {/* Commenting this out because it's currently not working for Vercel release. */}
                    <a
                        className="btn-container__button"
                        aria-label="Reset button"
                        data-testid="refresh-link"
                        href={NAVIGATION_URLS.legacy.base}
                    >
                        Continue shopping
                    </a>
                    {/* )} */}
                </div>
            </div>
        </div>
    );
}
